import { cache } from '@sravni/ugc/lib/utils/cache';

import { OrganizationType } from '../../interfaces/organizations';
import { DecoratedTagsByOrganizationType, ExtendedTag, IFiltersList } from '../../interfaces/reviews';
import ReviewsClient from '../../services/clients/ReviewsClient';
import {
  fetchDepositProducts,
  fetchEducationProducts,
  fetchMortgageProducts,
  IFetchProductsPublicParams,
  fetchVitrinsProducts,
} from '../services/products';

type GetProductsQuery = {
  tagAlias: string;
  id: string;
  type: OrganizationType;
};

type GetAllProductsQuery = {
  organizationId: string;
  type: OrganizationType;
};

export type TProduct = {
  name: string;
  id: string;
};

const decorateProducts = (items): TProduct[] => {
  if (items?.length) {
    return items.map((item) => ({ name: item.name, id: (item._id || item.id).toString() }));
  }
  return [];
};

export const getDepositProducts = async (organizationId) => {
  const params = {
    ...(organizationId && { organizations: organizationId }),
    adsOnly: false,
    currency: 'RUB',
    status: 'active',
  };

  return await fetchDepositProducts(params);
};

export const getMortgageProducts = async (organizationId) => {
  const params = {
    ...(organizationId && { organizations: organizationId }),
    productName: 'ipoteka',
    advertisingOnly: false,
    status: 'active',
  };

  return await fetchMortgageProducts(params);
};

export const getVitrinsProducts = async (organizationId, productName) => {
  const params = {
    productName,
    status: 'active',
    fields: ['name', 'id'],
    limit: 50,
    organizations: organizationId,
  };

  return await fetchVitrinsProducts(params);
};

export const getEducationProducts = async (organizationId, tag) => {
  const params = {
    status: 'active',
    limit: 100,
    fields: ['name', 'id', 'learningtype'],
    organizations: [organizationId],
    learningtype: tag,
  };

  return await fetchEducationProducts(params);
};

export const getProductsByTag = async (ctx: App.ExtendedContext<null, GetProductsQuery>) => {
  const { tagAlias, id, type, tag } = ctx.query;

  let items = [];

  if (type === OrganizationType.SCHOOL) {
    const getProducts = tag === 'kursy-ege' ? fetchVitrinsProducts : getEducationProducts;
    const data = await getProducts(id, tagAlias ?? tag);
    items = data?.items;
  } else {
    if (tag === 'savings') {
      const data = await getDepositProducts(id);

      items = data?.items;
    } else if (tag === 'mortgage') {
      const data = await getMortgageProducts(id);
      items = data?.items;
    } else {
      const vitrinsTagsConfig: ExtendedTag[] = cache.get(decoratedTagsCacheKey)?.[type] || [];
      const tagFromConfig = vitrinsTagsConfig?.find((tagConfig) => tagConfig.key === tag);
      const vitrinsProductName = tagFromConfig?.vitrinsProductName;

      if (vitrinsProductName) {
        const data = await getVitrinsProducts(id, vitrinsProductName);

        items = data?.items;
      }
    }
  }

  ctx.body = decorateProducts(items);
};

export const getProductsByTagCacheKey = ({
  query: { id, tagAlias, tag },
}: App.ExtendedContext<null, GetProductsQuery>) => {
  return `products-${tag}-${tagAlias}-${id}`;
};

const decoratedTagsCacheKey = 'decorated-tags';
export const putDecoratedTagsToCache = (decoratedTags: DecoratedTagsByOrganizationType) => {
  if (cache.get(decoratedTagsCacheKey)) return;

  cache.set(decoratedTagsCacheKey, decoratedTags);
};

export const getAllProducts = async (ctx: App.ExtendedContext<null, GetAllProductsQuery>) => {
  const { organizationId, type } = ctx.query;

  let tags = [];

  if (type === OrganizationType.SCHOOL) {
    const { data } = await ReviewsClient.get<IFiltersList>(`/v2/settings/filter/${OrganizationType.SCHOOL}`);
    tags = data?.tag || [];
  } else {
    tags = cache.get(decoratedTagsCacheKey)?.[type] || [];
  }

  const processTag = async (tag: { key: string; vitrinsProductName?: string }): Promise<[string, any[]] | null> => {
    let productsPromise;
    let allProductsName = 'Все продукты';

    if (type === OrganizationType.SCHOOL) {
      productsPromise = getEducationProducts(organizationId, tag.key);
      allProductsName = 'Все курсы';
    } else {
      if (tag.key === 'savings') {
        productsPromise = getDepositProducts(organizationId);
        allProductsName = 'Все вклады';
      } else if (tag.key === 'mortgage') {
        productsPromise = getMortgageProducts(organizationId);
      } else if (tag.vitrinsProductName) {
        productsPromise = getVitrinsProducts(organizationId, tag.vitrinsProductName);
      } else {
        return null;
      }
    }

    const res = await productsPromise;
    if (!res?.items?.length) return null;

    return [tag.key, [{ name: allProductsName, id: 'All' }, ...decorateProducts(res.items)]];
  };

  const promises = tags.map(processTag);
  const promisesResult = await Promise.all(promises);

  // @ts-ignore
  ctx.body = Object.fromEntries(promisesResult.filter(Boolean));
};

export const getAllProductsCacheKey = ({
  query: { organizationId },
}: App.ExtendedContext<null, { organizationId?: string }>) => {
  return organizationId ? `products-all-${organizationId}` : 'products-all';
};

export const getEducationProductsList = async (ctx: App.ExtendedContext<null, null, IFetchProductsPublicParams>) => {
  ctx.body = await fetchEducationProducts(ctx.request.body);
};
